import { useEffect, useRef, useState } from "react";
import { parseMarkdown, type Block, type Inline } from "@instead/shared";
import { useT } from "../lib/i18n.ts";
import { Ic } from "./Icons.tsx";

/**
 * The description editor (D-22 subset: heading, bold, italic, list, link)
 * shown formatted while you type — a contentEditable that reads and writes
 * the markdown we store (round 5: no raw asterisks on screen). The toolbar
 * appears only while the editor has focus.
 */
export function MarkdownEditor({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (md: string) => void }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const saved = useRef<Range | null>(null);

  // external value → editor, only when it isn't what the editor already holds
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    if (htmlToMd(el) !== value) el.innerHTML = mdToHtml(value);
  }, [value]);

  const emit = () => { if (ref.current) onChange(htmlToMd(ref.current)); };
  const exec = (cmd: string, arg?: string) => { ref.current?.focus(); document.execCommand(cmd, false, arg); emit(); };
  const blockTag = (): string => {
    const sel = window.getSelection();
    let n: Node | null = sel?.anchorNode ?? null;
    while (n && n !== ref.current) {
      if (n instanceof HTMLElement && /^(H[1-6]|P|DIV|LI)$/.test(n.tagName)) return n.tagName;
      n = n.parentNode;
    }
    return "P";
  };
  const heading = () => exec("formatBlock", blockTag() === "H3" ? "p" : "h3");
  const openLink = () => {
    const sel = window.getSelection();
    saved.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    setLinkUrl("https://");
    setLinkOpen(true);
  };
  const applyLink = () => {
    const url = linkUrl.trim();
    setLinkOpen(false);
    if (!/^https?:\/\/\S+$/.test(url)) return;
    ref.current?.focus();
    const sel = window.getSelection();
    if (saved.current && sel) { sel.removeAllRanges(); sel.addRange(saved.current); }
    if (!sel || sel.isCollapsed) document.execCommand("insertHTML", false, `<a href="${url.replace(/"/g, "")}">${escapeHtml(url)}</a>`);
    else document.execCommand("createLink", false, url);
    emit();
  };

  return (
    <div ref={wrap} className="field"
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => { if (!wrap.current?.contains(e.relatedTarget as Node | null)) { setFocused(false); setLinkOpen(false); } }}>
      <label htmlFor={id}>{label}</label>
      {focused && (
        <div className="chip-row md-tools" role="toolbar" aria-label={label} style={{ marginBottom: "var(--sp-1)" }}>
          <button type="button" className="chip" onMouseDown={(e) => e.preventDefault()} onClick={heading}>{t("desc.heading")}</button>
          <button type="button" className="chip" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} aria-label={t("desc.bold")}><strong>B</strong></button>
          <button type="button" className="chip" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} aria-label={t("desc.italic")}><em>I</em></button>
          <button type="button" className="chip" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")}>• {t("desc.list")}</button>
          <button type="button" className="chip" onMouseDown={(e) => e.preventDefault()} onClick={openLink}>{Ic.link({ size: 12 })} {t("desc.link")}</button>
          {linkOpen && (
            <span style={{ display: "inline-flex", gap: "var(--sp-1)", flexBasis: "100%" }}>
              <input className="input" type="url" inputMode="url" aria-label={t("desc.link")} autoFocus value={linkUrl} style={{ flex: 1, minHeight: 36 }}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyLink(); } if (e.key === "Escape") setLinkOpen(false); }} />
              <button type="button" className="btn" style={{ minHeight: 36 }} onMouseDown={(e) => e.preventDefault()} onClick={applyLink}>{t("add")}</button>
            </span>
          )}
        </div>
      )}
      <div id={id} ref={ref} className="input md-editor" contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" aria-label={label}
        onInput={emit}
        onKeyDown={(e) => {
          // Enter after a heading starts a plain line, not another heading
          if (e.key === "Enter" && !e.shiftKey && blockTag() === "H3") {
            e.preventDefault();
            document.execCommand("insertParagraph");
            document.execCommand("formatBlock", false, "p");
            emit();
          }
        }}
        onPaste={(e) => { e.preventDefault(); document.execCommand("insertText", false, e.clipboardData.getData("text/plain")); emit(); }} />
    </div>
  );
}

/* ---------- markdown → html (from the shared parser; nothing else) ---------- */

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function inlinesToHtml(inlines: Inline[]): string {
  return inlines.map((i) => {
    if (i.kind === "bold") return `<strong>${escapeHtml(i.text)}</strong>`;
    if (i.kind === "italic") return `<em>${escapeHtml(i.text)}</em>`;
    if (i.kind === "link") return `<a href="${escapeHtml(i.href)}">${escapeHtml(i.text)}</a>`;
    return escapeHtml(i.text);
  }).join("") || "<br>";
}
export function mdToHtml(md: string): string {
  const blocks: Block[] = parseMarkdown(md);
  if (blocks.length === 0) return "<p><br></p>";
  return blocks.map((b) => {
    if (b.kind === "heading") { const tag = (["h3", "h4", "h5"] as const)[b.level - 1] ?? "h5"; return `<${tag}>${inlinesToHtml(b.inlines)}</${tag}>`; }
    if (b.kind === "list") return `<ul>${b.items.map((it) => `<li>${inlinesToHtml(it)}</li>`).join("")}</ul>`;
    return `<p>${inlinesToHtml(b.inlines)}</p>`;
  }).join("");
}

/* ---------- html → markdown (what the browser made of the editing) ---------- */

function inlineMd(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replace(/\s+/g, " ");
  if (!(node instanceof HTMLElement)) return "";
  const inner = Array.from(node.childNodes).map(inlineMd).join("");
  const tag = node.tagName;
  if (tag === "BR") return "\n";
  if (tag === "STRONG" || tag === "B" || node.style.fontWeight === "bold" || parseInt(node.style.fontWeight || "0", 10) >= 600) return inner.trim() ? `**${inner.trim()}**` : "";
  if (tag === "EM" || tag === "I" || node.style.fontStyle === "italic") return inner.trim() ? `*${inner.trim()}*` : "";
  if (tag === "A") { const href = node.getAttribute("href") ?? ""; return /^https?:\/\//.test(href) && inner.trim() ? `[${inner.trim()}](${href})` : inner; }
  return inner;
}
export function htmlToMd(root: HTMLElement): string {
  const out: string[] = [];
  const push = (line: string) => { if (line.trim() || out.length) out.push(line.trim()); };
  for (const node of Array.from(root.childNodes)) {
    if (!(node instanceof HTMLElement)) { push(inlineMd(node)); continue; }
    const tag = node.tagName;
    const level = /^H([1-6])$/.exec(tag);
    if (level) push(`${"#".repeat(Math.min(3, Math.max(1, parseInt(level[1]!, 10) - 2 || parseInt(level[1]!, 10))))} ${inlineMd(node)}`);
    else if (tag === "UL" || tag === "OL") { for (const li of Array.from(node.children)) out.push(`- ${inlineMd(li).trim()}`); out.push(""); }
    else if (tag === "LI") out.push(`- ${inlineMd(node).trim()}`);
    else { const text = inlineMd(node); out.push(text.trim()); out.push(""); }
  }
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
